import React from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import type { IInPersonCandidate } from './utils';

interface IProps {
  signer: IInPersonCandidate;
  onConfirm: () => void;
  onCancel: () => void;
  isStarting?: boolean;
  error?: string | null;
}

function shortName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] ?? '';
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
}

export function HandOverDeviceModal({ signer, onConfirm, onCancel, isStarting, error }: IProps) {
  return (
    <Dialog
      open
      onClose={isStarting ? undefined : onCancel}
      classes={{ paper: 'max-w-[620px] w-full pb-4 px-8 pt-10' }}
      aria-labelledby="hand-over-title"
      data-spec="hand-over-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mb-10">
        <Typography id="hand-over-title" className="text-3xl font-bold font-serif" component="h2">
          Hand the device to {shortName(signer.name)}
        </Typography>
        <IconButton
          className="text-grey-500 absolute top-2 right-2"
          onClick={onCancel}
          disabled={isStarting}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {error ? (
        <Typography className="mb-4 text-red-500" role="alert" data-spec="hand-over-error">
          {error}
        </Typography>
      ) : null}

      <Typography>
        {signer.firstName} must be in person to sign. Please hand them the device and have them confirm they&rsquo;re
        ready to start signing in person.
      </Typography>

      <div className="mt-8 flex items-center justify-end gap-4 border-t border-grey-300 pt-4">
        <Button color="primary" onClick={onCancel} disabled={isStarting} data-spec="hand-over-cancel">
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={onConfirm}
          disabled={isStarting}
          data-spec="hand-over-start-signing"
        >
          {isStarting ? 'Starting…' : 'Start Signing'}
        </Button>
      </div>
    </Dialog>
  );
}
